import React, { useState, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, Platform, Dimensions, NativeSyntheticEvent, NativeScrollEvent } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import PageHeader from '@/components/PageHeader';
import WidgetPreview, { SmallWidgetPreview } from '@/components/WidgetPreview';
import CarouselPagination from '@/components/CarouselPagination';
import { useLanguage } from '@/contexts/LanguageContext';

const SCREEN_WIDTH = Dimensions.get('window').width;

const SLIDES = [
  { key: 'medium', labelKey: 'widget.preview.label' },
  { key: 'small', labelKey: 'widget.preview.labelSmall' },
];

function StepItem({ number, text }: { number: number; text: string }) {
  return (
    <View style={styles.stepRow}>
      <View style={styles.stepBadge}>
        <Text style={styles.stepNumber}>{number}.</Text>
      </View>
      <Text style={styles.stepText}>{text}</Text>
    </View>
  );
}

export default function WidgetPreviewScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);

  const platform = Platform.OS === 'ios' ? 'ios' : 'android';
  const stepKeys = platform === 'ios'
    ? ['step1', 'step2', 'step3', 'step4', 'step5']
    : ['step1', 'step2', 'step3', 'step4'];

  const onScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const index = Math.round(e.nativeEvent.contentOffset.x / SCREEN_WIDTH);
    setActiveIndex(index);
  }, []);

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader
        title={t('widget.preview.title')}
        onBack={() => router.back()}
        backgroundColor="#F9F8F3"
      />

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.carouselSection}>
          <ScrollView
            horizontal
            pagingEnabled
            showsHorizontalScrollIndicator={false}
            onScroll={onScroll}
            scrollEventThrottle={16}
            decelerationRate="fast"
            bounces={false}
            style={styles.carouselScroll}
            contentContainerStyle={styles.carouselContent}
          >
            <View style={styles.slide}>
              <View style={styles.widgetShadowWrapper}>
                <WidgetPreview />
              </View>
            </View>
            <View style={styles.slide}>
              <View style={styles.widgetShadowWrapper}>
                <SmallWidgetPreview />
              </View>
            </View>
          </ScrollView>

          <CarouselPagination count={SLIDES.length} activeIndex={activeIndex} style={styles.pagination} />
          <Text style={styles.label}>{t(SLIDES[activeIndex].labelKey)}</Text>
        </View>

        <View style={styles.instructionsSection}>
          <Text style={styles.instructionsTitle}>
            {t('widget.guide.title')}
          </Text>

          <View style={styles.stepsContainer}>
            {stepKeys.map((key, index) => (
              <StepItem
                key={key}
                number={index + 1}
                text={t(`widget.guide.${platform}.${key}`)}
              />
            ))}
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },
  content: {
    flexGrow: 1,
    gap: 32,
    paddingBottom: 24,
  },
  carouselSection: {
    alignItems: 'center',
    paddingTop: 24,
  },
  carouselScroll: {
    flexGrow: 0,
  },
  carouselContent: {
    paddingVertical: 48,
  },
  slide: {
    width: SCREEN_WIDTH,
    alignItems: 'center',
    justifyContent: 'center',
  },
  widgetShadowWrapper: {
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 24 },
    shadowOpacity: 0.08,
    shadowRadius: 32,
    elevation: 12,
    borderRadius: 24,
  },
  pagination: {
    marginTop: 8,
  },
  label: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#A19F97',
    textAlign: 'center',
    marginTop: 12,
  },
  instructionsSection: {
    width: '100%',
    paddingHorizontal: 24,
    paddingTop: 16,
    gap: 24,
  },
  instructionsTitle: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 14 * 1.4,
  },
  stepsContainer: {
    gap: 24,
  },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
  },
  stepBadge: {
    width: 32,
    height: 32,
    borderRadius: 20,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepNumber: {
    color: '#7E7D75',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 14 * 1.4,
  },
  stepText: {
    flex: 1,
    color: '#151515',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 14 * 1.4,
  },
});
